/*
 * What chat does once the client runs (`FrontendModule.bind`, `core/modules/bind.ts`). The boot hands
 * every module the store, the outbox and the socket's request; chat turns them into five
 * things:
 *
 *   · the surface is given the calls behind its actions, each of which goes through the outbox so
 *     that an undo and a reload both work (`deps.tsx`);
 *   · chat's two outbox key prefixes are given the socket frame each item turns into, and the server
 *     mirror of a draft is installed over chat's routes and requests;
 *   · the store learns how to refetch one conversation's tail when it finds a gap, because core never
 *     names a module's URL;
 *   · a message whose rendered tree did not fit into its event has that tree fetched;
 *   · the echo of the author's own message acknowledges the outbox item it came from, so the second
 *     tab shows one row rather than two (spec 08 §6.3);
 *   · core's emoji picker is given the person's emoji history, which chat keeps on the server, and
 *     the recent-emoji list follows the server's pushes.
 *
 * It is reached statically from the entry, through the module object, because the boot binds before
 * the first render; what it imports is the module's plumbing and no screen
 * (`core/modules/entryClosure.test.ts`).
 */

import {
	appDraftStore,
	installDraftMirror,
	keyOf,
	registerOutboxTransport,
} from "../../core/drafts/index.ts"
import { applyEmojiPrefs, provideEmojiHistory } from "../../core/emoji/recents.ts"
import type { ModuleBindings } from "../../core/modules/registry.ts"
import { writeAccountSetting } from "../../core/shell/index.ts"
import type { ClientStore } from "../../core/store/store.ts"
import type { RequestType } from "../../core/wire/index.ts"
import { draftMirrorTransport, fetchEmojiRecents } from "./api.ts"
import { setChatDeps } from "./deps.tsx"
import { CHAT_OUTBOX_PREFIXES, chatOutboxRequest } from "./outboxTransport.ts"
import { resyncConversation } from "./state/history.ts"
import { installRenderRefetch } from "./state/renderRefetch.ts"
import { chatSlice } from "./state/selectors.ts"

/** The setting the picker's tone row writes; the same key the server registers (emoji/recents.go). */
const SKIN_TONE_SETTING = "chat.emoji.skin_tone"

/**
 * The version the cursor of one conversation last had here. The cursor is optimistically locked:
 * the version the client last saw travels with every move, and a cursor another tab moved meanwhile
 * is refused rather than overwritten.
 */
function cursorVersion(client: ClientStore, conv: number): number {
	return chatSlice(client.api.getState()).cursors[conv]?.version ?? 0
}

/** Binds chat to the running client; answers the unsubscribe of the listeners it registered. */
export function bindChat(b: ModuleBindings): () => void {
	setChatDeps({
		send: async ({ conv, markdown, attachments, threadRootId, alsoToChannel }) => {
			// A reply is sent under the thread's own target, which is also the key its draft was
			// typed under (`core/drafts/targetKey.ts`): the outbox queues one thread independently
			// of the conversation, and `chatOutboxRequest` reads the root back out of the key.
			const target = keyOf(
				threadRootId === undefined
					? { kind: "conversation", conv }
					: { kind: "thread", conv, threadRootId },
			)
			// "Also send to #channel" rides on the item and not on its target: the draft stays the
			// thread's one draft, and the destination is a fact about this one reply (X-A1 A4).
			await b.outbox.enqueue(
				target,
				{ kind: "post", markdown, attachments, ...(alsoToChannel === true && { alsoToChannel }) },
				target,
			)
		},
		// An edit carries the whole attachment set, not the text alone: the server takes the set it
		// is given as the message's files, so an edit that named none would unbind every file the
		// message has (I42).
		edit: async ({ id, markdown, attachments, baseVersion }) => {
			await b.outbox.enqueue(`e:${id}`, {
				kind: "edit",
				messageId: id,
				markdown,
				attachments,
				baseVersion,
			})
		},
		remove: async ({ id }) => {
			await b.outbox.enqueue(`e:${id}`, { kind: "delete", messageId: id })
		},
		markUnreadFrom: async ({ conv, seq }) => {
			await b.request("chat.read.markUnreadFrom", {
				conv,
				seq,
				version: cursorVersion(b.client, conv),
			})
		},
		// The other end of the same cursor: the reader has seen up to `seq`, so the badge comes
		// down. `auto` says this was the viewport rule and not a click, which is what lets a
		// deliberate "mark unread from here" survive the reader's own scrolling (protocol.md
		// §2.5.5).
		markRead: async ({ conv, seq, auto }) => {
			await b.request("chat.read.mark", { conv, seq, version: cursorVersion(b.client, conv), auto })
		},
		// A reaction is not a message and never goes through the outbox: it carries no text to lose,
		// it has no undo window, and the unique row on the server is its own idempotency, so a
		// repeat answers the current summary instead of adding a second one (protocol.md §2.5.4).
		react: async ({ messageId, emoji, on }) => {
			await b.request(on ? "chat.reaction.add" : "chat.reaction.remove", { messageId, emoji })
		},
		// Typing is ephemeral and fire-and-forget: never queued, never retried, never held. The
		// composer core throttles it to one frame per `typingIntervalMs` per target and the chat
		// module decides which targets are worth one (`conversation/TypingLine.tsx`).
		typing: async ({ conv, threadRootId }) => {
			await b.request("chat.typing", { conv, ...(threadRootId !== undefined && { threadRootId }) })
		},
	})

	// Chat's outbox items leave as socket frames: the outbox is core's and knows no wire, so chat's
	// two key prefixes are given the frame each item turns into (`outboxTransport.ts`).
	const stopTransports = CHAT_OUTBOX_PREFIXES.map((prefix) =>
		registerOutboxTransport(prefix, (item) => {
			const { type, data } = chatOutboxRequest(item)
			return b.request(type as RequestType, data)
		}),
	)

	// The server mirror of a draft (protocol.md §2.5.6): the rules are core's, the two routes and the
	// two socket requests are chat's, so the mirror is installed here, where both exist. It reads the
	// mirror when the socket opens and never overwrites a box that has text in it.
	const mirror = installDraftMirror({
		store: appDraftStore(),
		client: b.client,
		transport: draftMirrorTransport(b.request),
	})

	// The picker is core's and the person's emoji history chat's: the list lives in chat_emoji_use
	// behind `GET /api/chat/emoji/recents` and `chat.emoji.used`, the tone in the setting chat
	// registers (`emoji/recents.go`). Recording a use is fire and forget (protocol.md §2.5.4): a
	// history that did not save is a row in a different order, nothing a person should be told about.
	const stopEmojiHistory = provideEmojiHistory({
		load: async () => {
			const view = await fetchEmojiRecents()
			return { recents: view.emoji, skinTone: view.skinTone }
		},
		note: (emoji) => {
			void b.request("chat.emoji.used", { emoji: [...emoji] }).catch(() => undefined)
		},
		saveTone: (tone) => {
			writeAccountSetting(b.client.api, SKIN_TONE_SETTING, tone)
		},
	})

	// A resync drops the loaded threads before it refetches the tail: the window they were read
	// against is gone, and an open pane asks for its replies again (UX §4.6).
	b.client.setResyncHandlers({ conversation: resyncConversation })

	// A message whose rendered tree is larger than a change_log row may hold travels without it, and
	// this fetches the tree for the windows that are holding the message (state/renderRefetch.ts).
	const stopRenderRefetch = installRenderRefetch(b.client)

	const stopEcho = b.client.onEvent((event) => {
		// The recent-emoji list is the picker's own store and not model state, so the event is
		// applied here rather than in the reducer (I53).
		if (event.type === "chat.emoji.recents") {
			applyEmojiPrefs({ recents: event.data.emoji, skinTone: event.data.skinTone })
			return
		}
		if (event.type !== "chat.message.new") return
		const { clientMsgId } = event.data
		if (clientMsgId) void b.outbox.acknowledged(clientMsgId)
	})

	return () => {
		stopEmojiHistory()
		for (const stop of stopTransports) stop()
		mirror.stop()
		stopRenderRefetch()
		stopEcho()
	}
}
